import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {Collapse, Stack, Typography} from '@mui/material';
import {FC, memo, useEffect, useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {subArrsByKey} from '../../../../shared/helpers/subArrsByKey';
import {IContentTreeItem, IWorkContentFull} from '../../../../shared/types/workContent.types';
import {getDefaultValuesWorkData} from './utils';
import {WorkDataItemContent} from './WorkDataItemContent';

import {authState} from '../../../../store/auth/atoms';
import {baseWorkData, editableWorkData, serviceRequestActiveTabState} from '../../../../store/service-requests/atoms';

import {checkAuthorities} from '../../../../shared/helpers/checkAuthorities';

import {flattenDeep, get, sumBy} from 'lodash';
import {TypeAccountRoles} from '../../../../shared/types/user-account.types';
import {getFileWord} from '../../../../shared/utils/file';
import {statusColors} from '../../../../shared/colors';

interface IWorkDataItem {
  item: IContentTreeItem;
}

const DEFAULT_POSSIBLE_ROLES: TypeAccountRoles[] = [TypeAccountRoles.MANAGER, TypeAccountRoles.WORKER];

function renderDocsLength(docsLength: number) {
  return (
    <span style={{color: docsLength ? statusColors.CREATED.text : 'inherit'}}>
      ({docsLength} {getFileWord(docsLength)})
    </span>
  );
}

const WorkDataItemBase: FC<IWorkDataItem> = ({item}) => {
  const activeTab = useRecoilValue(serviceRequestActiveTabState);
  const setEditableWorkData = useSetRecoilState(editableWorkData);
  const setBaseWorkData = useSetRecoilState(baseWorkData);
  const {user} = useRecoilValue(authState);
  const [open, setOpen] = useState(false);

  const subArrs = useMemo(() => {
    const arrs = subArrsByKey<IWorkContentFull>(item.workContents || [], 'type');
    return arrs.length ? arrs : item.workContents ? [item.workContents] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, activeTab?.editMode]);

  const availableForEdit = item.availableForEdit.length ? item.availableForEdit : DEFAULT_POSSIBLE_ROLES;

  const isHaveAuthoritiesToEditStage = checkAuthorities(availableForEdit, user?.roles as TypeAccountRoles[]);

  const formPart = useMemo(() => getDefaultValuesWorkData(subArrs), [subArrs]);

  const docsLength = useMemo(
    () => sumBy(flattenDeep(subArrs), (item) => get(item, 'item.documents.length', 0)),
    [subArrs],
  );

  const defaultValues = useMemo(
    () => Object.fromEntries(Object.entries(formPart).map((i) => [i[0], (i[1] as Record<string, any>).value])),
    [formPart],
  );

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
  });

  const data = methods.watch();

  useEffect(() => {
    setEditableWorkData((prev) => {
      return {
        ...prev,
        form: prev.form ? {...prev.form, ...data} : {...data},
        isDirty: methods.formState.isDirty && methods.formState.isValid,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, methods.formState.isDirty, methods.formState.isValid]);

  useEffect(() => {
    setBaseWorkData((prev) => ({...prev, ...formPart}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab?.editMode) methods.trigger();
    if (methods.formState.isDirty) methods.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab?.editMode]);

  return (
    <Stack style={{marginBottom: '20px'}}>
      <Stack
        direction="row"
        justifyContent="space-between"
        component="div"
        onClick={() => setOpen((prev) => !prev)}
        sx={{mb: '10px', alignItems: 'center', cursor: 'pointer'}}
      >
        <Typography variant="body1">
          {item.name} &nbsp; {renderDocsLength(docsLength)}
        </Typography>
        {open ? <ExpandLess /> : <ExpandMore />}
      </Stack>
      <Collapse in={open} timeout="auto">
        <FormProvider {...methods}>
          {subArrs.map((items) => (
            <WorkDataItemContent
              isEditable={isHaveAuthoritiesToEditStage}
              key={items[0].type}
              formPart={formPart}
              item={{type: items[0].type, fields: items}}
            />
          ))}
        </FormProvider>
      </Collapse>
    </Stack>
  );
};

export const WorkDataItem = memo(WorkDataItemBase, (prevProps, nextProps) => prevProps.item.id === nextProps.item.id);
